import { withTranslation } from "react-i18next";
import withRouter from "../../../../components/Common/withRouter";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import BreadCrumb from "../../../../components/Common/BreadCrumb";
import {
  Form,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormFeedback,
  Input,
  Label,
  Row,
  FormGroup,
} from "reactstrap";
import { createSelector } from "reselect";
import {
  getContributionTypes,
  getSalaryTypes,
  loadAllDeductionCategories as onLoadAllDeductionCategories,
  saveDeduction,
} from "../../../../slice/thunks";

const AddDeduction = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const errorMessage = "Failed to save deduction";

  // ================= SELECTOR =================
  const selectPayrollState = (state) => state.Payroll;

  const payrollSelector = createSelector(selectPayrollState, (payroll) => ({
    deductionCategories: payroll?.deductionCategories ?? [],
    contributionTypes: payroll?.contributionTypes ?? [],
    salaryTypes: payroll?.salaryTypes ?? [],
    error: payroll?.error ?? null,
  }));

  const { deductionCategories, contributionTypes, salaryTypes } =
    useSelector(payrollSelector);

  // ================= LOAD DATA =================
  const loadDeductionCategories = useCallback(() => {
    dispatch(onLoadAllDeductionCategories())
      .unwrap()
      .catch((error) => {
        toast.error(error.message || "Failed to load deduction categories");
        navigate("/payroll/deductions");
      });
  }, [dispatch, navigate]);

  useEffect(() => {
    loadDeductionCategories();
    dispatch(getContributionTypes());
    dispatch(getSalaryTypes());
  }, [dispatch, loadDeductionCategories]);

  // ================= SAVE HANDLER =================
  const onClickOnSave = useCallback(
    (values) => {
      setIsSubmitting(true);

      dispatch(saveDeduction(values))
        .unwrap()
        .then((response) => {
          toast.success(response.message || "Deduction saved successfully");
          navigate("/payroll/deductions");
        })
        .catch((error) => {
          setIsSubmitting(false);
          toast.error(error.message || errorMessage);
        });
    },
    [dispatch, navigate],
  );

  // ================= FORMIK =================
  const formik = useFormik({
    initialValues: {
      name: "",
      categoryId: "",
      contributionType: "",
      deductionType: "",
      rateOrAmount: "",
      employerContribution: false,
      appliedTo: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().required("Name of the deduction is required"),

      categoryId: Yup.string().required("Category is required"),

      contributionType: Yup.string().required(
        "Contribution type is required",
      ),

      deductionType: Yup.string()
        .oneOf(["FIXED", "VARIABLE"], "Must be FIXED or VARIABLE")
        .required("Deduction type is required"),

      rateOrAmount: Yup.number()
        .typeError("Rate / Amount must be a number")
        .positive("Value must be greater than 0"),
    }),
    onSubmit: (values) => {
      onClickOnSave(values);
    },
  });

  // ================= RENDER =================
  return (
    <div className="deductions px-3">
      <div className="patients-list py-4 px-2">
        <div className="container-fluid">
          <div className="row list-patients d-flex justify-content-between">
            <Link to="/payroll/deductions" className="col-lg-5">
              <BreadCrumb
                title={props.t("deductions")}
                pageTitle={props.t("add_deduction")}
              />
            </Link>
          </div>

          <Card className="card table-card">
            <CardHeader className="bg-light p-3">
              <h5>{props.t("add_deduction")}</h5>
            </CardHeader>

            <CardBody>
              <div className="container">
                <Form
                  autoComplete="off"
                  onSubmit={(e) => {
                    e.preventDefault();
                    formik.handleSubmit();
                  }}>
                  {/* NAME */}
                  <Row>
                    <Col md={12}>
                      <Label>
                        Name of deduction <span className="text-danger">*</span>
                      </Label>
                      <Input
                        name="name"
                        type="text"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.name && !!formik.errors.name}
                      />
                      <FormFeedback>{formik.errors.name}</FormFeedback>
                    </Col>
                  </Row>

                  {/* CATEGORY */}
                  <Row className="mt-3">
                    <Col md={12}>
                      <Label>
                        Category <span className="text-danger">*</span>
                      </Label>
                      <Input
                        name="categoryId"
                        type="select"
                        value={formik.values.categoryId}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.categoryId &&
                          !!formik.errors.categoryId
                        }>
                        <option value="">Select Category</option>
                        {deductionCategories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </Input>
                      <FormFeedback>{formik.errors.categoryId}</FormFeedback>
                    </Col>
                  </Row>

                  {/* CONTRIBUTION TYPE */}
                  <Row className="mt-3">
                    <Col md={12}>
                      <Label>
                        Contribution Type <span className="text-danger">*</span>
                      </Label>
                      <Input
                        name="contributionType"
                        type="select"
                        value={formik.values.contributionType}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.contributionType &&
                          !!formik.errors.contributionType
                        }>
                        <option value="">Select Contribution Type</option>
                        {contributionTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.title}
                          </option>
                        ))}
                      </Input>
                      <FormFeedback>
                        {formik.errors.contributionType}
                      </FormFeedback>
                    </Col>
                  </Row>

                  {/* APPLIED TO */}
                  <Row className="mt-3">
                    <Col md={12}>
                      <Label>
                        To deduct from <span className="text-danger">*</span>
                      </Label>
                      <Input
                        name="appliedTo"
                        type="select"
                        value={formik.values.appliedTo}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.appliedTo && !!formik.errors.appliedTo
                        }>
                        <option value="">Select Salary Type</option>
                        {salaryTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.title}
                          </option>
                        ))}
                      </Input>
                      <FormFeedback>{formik.errors.appliedTo}</FormFeedback>
                    </Col>
                  </Row>

                  {/* EMPLOYER CONTRIBUTION */}
                  <Row className="mt-3">
                    <Col md={12}>
                      <Label>Employer Contribution</Label>
                      <FormGroup switch>
                        <Input
                          type="switch"
                          name="employerContribution"
                          checked={formik.values.employerContribution}
                          onChange={formik.handleChange}
                        />
                        <Label check>Is Employer Contribution?</Label>
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* DEDUCTION TYPE */}
                  <Row className="mt-3">
                    <Col md={12}>
                      <Label>
                        Deduction Type <span className="text-danger">*</span>
                      </Label>
                      <Input
                        name="deductionType"
                        type="select"
                        value={formik.values.deductionType}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.deductionType &&
                          !!formik.errors.deductionType
                        }>
                        <option value="">Select Deduction Type</option>
                        <option value="FIXED">Fixed</option>
                        <option value="VARIABLE">Variable</option>
                      </Input>
                      <FormFeedback>{formik.errors.deductionType}</FormFeedback>
                    </Col>
                  </Row>

                  {/* RATE / AMOUNT */}
                  <Row className="mt-3">
                    <Col md={12}>
                      <Label>Rate / Amount</Label>
                      <Input
                        name="rateOrAmount"
                        type="number"
                        value={formik.values.rateOrAmount}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.rateOrAmount &&
                          !!formik.errors.rateOrAmount
                        }
                      />
                      <FormFeedback>{formik.errors.rateOrAmount}</FormFeedback>
                    </Col>
                  </Row>

                  {/* BUTTONS */}
                  <Row className="align-items-center p-3">
                    <Col md={6}>
                      <span className="text-danger">* : Required fields</span>
                    </Col>
                    <Col md={6} className="text-end">
                      <Link
                        to="/payroll/deductions"
                        className="btn btn-secondary mx-2">
                        Cancel
                      </Link>

                      <button
                        type="submit"
                        className="btn btn-primary mx-2"
                        disabled={isSubmitting || !formik.isValid}>
                        {isSubmitting ? "Saving..." : "Save"}
                      </button>
                    </Col>
                  </Row>
                </Form>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default withRouter(withTranslation()(AddDeduction));
